import { useState, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShakeAnimation, XPFloat, ConfettiBurst, TappableCard } from "@/utils/animations";
import { PetAvatar } from "@/components/PetAvatar";
import { DoodleQuestion, DoodleStar } from "@/components/Doodles";
import { playCorrect, playWrong, playClick } from "@/utils/sounds";

const stripMarkdown = (text) => text?.replace(/\*\*/g, "") || "";

const LETTER_LABELS = ["A", "B", "C"];

export const MasteryQuiz = ({ questions, user, onSubmit, onComplete }) => {
  const [currentIdx, setCurrentIdx]   = useState(0);
  const [answers, setAnswers]         = useState(Array(3).fill(null));
  const [showFeedback, setShowFeedback] = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [masteryResult, setMasteryResult] = useState(null);
  const [petState, setPetState]       = useState("idle");
  const [earnedXP, setEarnedXP]       = useState(0);
  const timerRef = useRef(null);
  const answersRef = useRef(answers);

  const q             = questions[currentIdx];
  const selectedAnswer = answers[currentIdx];
  const isCorrect     = selectedAnswer === q?.correct_index;
  const finalScore    = answers.filter((a, i) => a === questions[i]?.correct_index).length;

  const advanceToNext = useCallback(async (currentAnswers) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const ans = currentAnswers || answersRef.current;
    if (currentIdx === 2) {
      const score = ans.filter((a, i) => a === questions[i]?.correct_index).length;
      setSubmitted(true);
      setShowFeedback(false);
      setPetState("celebrating");
      setSubmitting(true);
      let result = null;
      try {
        result = await onSubmit(score, 3);
        setMasteryResult(result);
      } catch (err) { console.warn('Quiz:', err?.message || err); }
      setSubmitting(false);
      const wrongAnswers = questions.map((qq, i) => {
        if (ans[i] !== qq.correct_index) {
          return {
            question: stripMarkdown(qq.question),
            correctOption: stripMarkdown(qq.options[qq.correct_index]),
            explanation: qq.priya_explains || qq.explanation,
          };
        }
        return null;
      }).filter(Boolean);
      if (onComplete) onComplete(score, wrongAnswers, result);
    } else {
      setShowFeedback(false);
      setPetState("happy");
      setCurrentIdx(prev => prev + 1);
    }
  }, [currentIdx, questions, onSubmit, onComplete]);

  const selectAnswer = (optIdx) => {
    if (showFeedback || submitted || selectedAnswer !== null) return;
    const newAnswers = [...answers];
    newAnswers[currentIdx] = optIdx;
    setAnswers(newAnswers);
    answersRef.current = newAnswers;
    setShowFeedback(true);

    const correct = optIdx === q?.correct_index;
    if (correct) {
      setPetState("excited");
      setEarnedXP(prev => prev + 5);
      playCorrect();
    } else {
      setPetState("encouraging");
      playWrong();
    }

    timerRef.current = setTimeout(() => advanceToNext(newAnswers), 4000);
  };

  const handleRetake = () => {
    playClick();
    if (timerRef.current) clearTimeout(timerRef.current);
    setCurrentIdx(0);
    setAnswers(Array(3).fill(null));
    answersRef.current = Array(3).fill(null);
    setShowFeedback(false);
    setSubmitted(false);
    setMasteryResult(null);
    setPetState("happy");
    setEarnedXP(0);
  };

  const scoreMessage = (s) => {
    if (s === 3) return "You really understood this! 🏆";
    if (s === 2) return "Great thinking! Almost perfect! 🌟";
    if (s === 1) return "Good try! Every question taught you something new! 👍";
    return "That was tough! But now you know more! 💪";
  };

  return (
    <div
      style={{ background: '#FFFEF9', borderRadius: 4, overflow: 'hidden', width: '100%', position: 'relative', border: '1.5px dashed #CCCCCC' }}
      data-testid="mastery-quiz"
    >
      <style>{`
        @keyframes xpFloat {
          0%   { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-28px) scale(1.1); }
        }
      `}</style>

      {/* ConfettiBurst layer */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
        <ConfettiBurst trigger={showFeedback && isCorrect} colors={['#66BB6A','#FFD740','#448AFF','#66BB6A']} />
      </div>

      {/* Doodle decorations */}
      <div style={{ position: 'absolute', top: 10, right: 10, pointerEvents: 'none', zIndex: 20 }}>
        <DoodleQuestion color="#448AFF" opacity={0.15} size={14} />
      </div>
      <div style={{ position: 'absolute', bottom: 10, left: 10, pointerEvents: 'none', zIndex: 20 }}>
        <DoodleStar color="#FFD740" opacity={0.2} size={10} />
      </div>

      <div style={{ padding: '14px 14px 16px' }}>

        {/* 1 — Pet header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <PetAvatar
            petType={localStorage.getItem('you_pet_type') || 'elephant'}
            stage={parseInt(localStorage.getItem('you_pet_stage') || '1')}
            mood={submitted ? 'celebrating' : petState}
            size={40}
            animated
          />
          <div style={{ background: 'transparent', borderRadius: 10, padding: '5px 10px', border: '1.5px dashed #448AFF' }}>
            <p style={{ fontSize: 14, color: '#666666', margin: 0 }}>
              {submitted ? "Well done! 🎉" : "What would happen if... 🤔"}
            </p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!submitted ? (
            <motion.div
              key={currentIdx}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.22 }}
            >
              {/* 2 — Progress */}
              <div style={{ marginBottom: 10 }}>
                <p style={{ fontSize: 15, color: '#4A4A5A', margin: '0 0 5px 0' }}>
                  Question {currentIdx + 1} of 3
                </p>
                <div style={{ height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.04)', border: '1px dashed #CCCCCC', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 2,
                    background: '#448AFF',
                    width: `${((currentIdx + (showFeedback ? 1 : 0)) / 3) * 100}%`,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>

              {/* 3 — Question card */}
              <div style={{ background: '#FFFFFF', borderRadius: 4, padding: 12, marginBottom: 10, border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #448AFF', transform: 'rotate(-0.5deg)' }}>
                <p style={{ fontSize: 24, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, color: '#1A1A2E', margin: 0, lineHeight: 1.5 }}>
                  {stripMarkdown(q?.question)}
                </p>
              </div>

              {/* 4 — Answer options */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 10 }}>
                {q?.options.map((opt, oIdx) => {
                  const isSelected    = selectedAnswer === oIdx;
                  const showCorrect   = showFeedback && oIdx === q.correct_index;
                  const showWrong     = showFeedback && isSelected && !isCorrect;

                  const borderColor = showCorrect ? '#66BB6A'
                    : showWrong ? '#FF9800'
                    : isSelected ? '#448AFF'
                    : 'rgba(0,0,0,0.08)';

                  const circleBg = showCorrect ? '#66BB6A'
                    : showWrong ? 'rgba(255,152,0,0.2)'
                    : 'rgba(68,138,255,0.08)';

                  const circleColor = showCorrect ? '#fff'
                    : showWrong ? '#FF9800'
                    : 'rgba(68,138,255,0.7)';

                  const textColor = showCorrect ? '#66BB6A'
                    : showWrong ? '#E65100'
                    : '#1A1A2E';

                  return (
                    <ShakeAnimation key={oIdx} trigger={showWrong}>
                      <TappableCard
                        onClick={() => selectAnswer(oIdx)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '12px 16px', borderRadius: 4,
                          border: `1.5px dashed ${borderColor}`,
                          background: showCorrect ? 'rgba(102,187,106,0.06)'
                            : showWrong ? '#FFF3E0'
                            : '#FFFFFF',
                          transition: 'border-color 0.2s, background 0.2s',
                          position: 'relative', overflow: 'visible',
                          cursor: showFeedback ? 'default' : 'pointer',
                        }}
                      >
                        {/* XP Float on correct answer */}
                        {showCorrect && isSelected && (
                          <XPFloat key={`xp-${currentIdx}`} amount={5} />
                        )}
                        {/* Letter circle */}
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: circleBg, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 500, fontFamily: "'Inter', sans-serif", color: circleColor,
                          transition: 'background 0.2s',
                        }}>
                          {LETTER_LABELS[oIdx]}
                        </div>
                        {/* Answer text */}
                        <span style={{ fontSize: 16, fontWeight: 400, color: textColor, lineHeight: 1.4, transition: 'color 0.2s', flex: 1 }}>
                          {stripMarkdown(opt)}
                        </span>
                        {/* Not quite label for wrong */}
                        {showWrong && (
                          <span style={{ fontSize: 13, color: '#FF9800', fontFamily: "'Patrick Hand', cursive", whiteSpace: 'nowrap', flexShrink: 0 }}>
                            Not quite! 🤭
                          </span>
                        )}
                      </TappableCard>
                    </ShakeAnimation>
                  );
                })}
              </div>

              {/* 5 — Priya explains section */}
              <AnimatePresence>
                {showFeedback && selectedAnswer !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      padding: '12px 14px', borderRadius: 4, marginBottom: 10,
                      background: isCorrect ? 'rgba(102,187,106,0.06)' : '#FFFEF9',
                      border: `1.5px dashed ${isCorrect ? '#66BB6A' : '#FF9800'}`,
                      borderLeft: `3px solid ${isCorrect ? '#66BB6A' : '#FF9800'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ fontSize: 20, flexShrink: 0, lineHeight: 1 }}>🧑‍🏫</span>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          fontSize: 14, fontFamily: "'Patrick Hand', cursive", fontWeight: 400,
                          color: isCorrect ? '#2E7D32' : '#E65100', margin: '0 0 4px',
                        }}>
                          {isCorrect ? 'Priya explains:' : "Here's what would actually happen:"}
                        </p>
                        <p style={{ fontSize: 15, fontFamily: "'Inter', sans-serif", fontWeight: 400, color: '#555555', margin: 0, lineHeight: 1.6 }}>
                          {q?.priya_explains || q?.explanation}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                      <button
                        onClick={() => advanceToNext()}
                        style={{
                          padding: '8px 20px', borderRadius: 4,
                          border: '1.5px dashed #448AFF', background: 'transparent',
                          color: '#448AFF', fontSize: 14, fontFamily: "'Patrick Hand', cursive",
                          fontWeight: 400, cursor: 'pointer', minHeight: 44,
                        }}
                      >
                        {currentIdx === 2 ? 'See results →' : 'Next →'}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 6 — Score tracker */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', borderRadius: 4,
                background: '#FFFEF9', border: '1.5px dashed #CCCCCC',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, color: '#666666', fontWeight: 600 }}>Score</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {Array.from({ length: 3 }, (_, i) => {
                      const answered = answers[i] !== null;
                      const correct  = answered && answers[i] === questions[i]?.correct_index;
                      return (
                        <div key={i} style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: correct ? '#66BB6A' : answered ? 'rgba(255,152,0,0.5)' : 'rgba(0,0,0,0.08)',
                          border: correct ? 'none' : '1px solid rgba(0,0,0,0.08)',
                          transition: 'background 0.3s',
                        }} />
                      );
                    })}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 12 }}>⭐</span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#E6A800' }}>{earnedXP} XP</span>
                </div>
              </div>

              {!user && currentIdx === 0 && (
                <p style={{ fontSize: 11, textAlign: 'center', color: '#888888', marginTop: 8 }}>
                  Sign in to save your mastery progress
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ textAlign: 'center' }}
            >
              <p style={{ fontSize: 40, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, color: '#1A1A2E', margin: '0 0 4px 0' }}>{finalScore}/3</p>
              <p style={{ fontSize: 16, fontWeight: 500, color: '#E6A800', margin: '0 0 16px 0' }}>
                {scoreMessage(finalScore)}
              </p>
              {submitting && (
                <p style={{ fontSize: 11, color: '#666666', marginBottom: 12 }}>Saving your progress...</p>
              )}
              {masteryResult?.mastery_pct !== undefined && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 11, color: '#666666', marginBottom: 4 }}>
                    Topic mastery: {masteryResult.mastery_pct}%
                  </p>
                  <div style={{ height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${masteryResult.mastery_pct}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      style={{
                        height: '100%', borderRadius: 2,
                        background: masteryResult.mastery_pct >= 75 ? '#66BB6A'
                          : masteryResult.mastery_pct >= 50 ? '#448AFF' : '#FF6B6B',
                      }}
                    />
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
                {Array.from({ length: 3 }, (_, i) => (
                  <div key={i} style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: i < finalScore ? '#66BB6A' : 'rgba(0,0,0,0.08)',
                    border: i < finalScore ? 'none' : '1px solid rgba(0,0,0,0.08)',
                  }} />
                ))}
              </div>
              <button
                onClick={handleRetake}
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 12, fontSize: 16,
                  fontWeight: 400, color: '#448AFF',
                  fontFamily: "'Patrick Hand', cursive",
                  background: 'transparent', border: '1.5px dashed #448AFF',
                  cursor: 'pointer',
                }}
              >
                Retake Quiz
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
